/** Field definitions for analytics graph filters (`query_json` keys: asset, lessee, lessor, owner, currency, event_type, days). */
export const buildAnalyticsFilterMenu = ( assetList, lessee, owner, currencyOptions, eventTypeOptions ) => ({
  asset: {
    title: "Asset",
    inputType: "dropdown",
    options: (assetList || []).map((a) => ({ ...a, asset_key: `${a.asset_type}_${a.id}` })),
    valueKey: "asset_key",
    labelKey: "name",
    multiple: false,
    dependentFields: ["event_type"],
  },
  lessee: {
    title: "Lessee",
    inputType: "dropdown",
    options: lessee || [],
    valueKey: "id",
    labelKey: "name",
    multiple: true,
  },
  lessor: {
    title: "Lessor/Holdco",
    inputType: "dropdown",
    options: owner || [],
    valueKey: "id",
    labelKey: "name",
    multiple: true,
  },
  owner: {
    title: "Owner/SPV",
    inputType: "dropdown",
    options: owner || [],
    valueKey: "id",
    labelKey: "name",
    multiple: true,
  },
  currency: {
    title: "Currency",
    inputType: "dropdown",
    options: currencyOptions || [],
    valueKey: "code",
    labelKey: "code",
    multiple: false,
  },
  event_type: {
    title: "Event Type",
    inputType: "dropdown",
    options: eventTypeOptions || [],
    valueKey: "id",
    labelKey: "name",
    multiple: false,
  },
  days: {
    title: "Expiry Days",
    inputType: "dropdown",
    options: [
      { id: 1, name: "10 days", value: "10" },
      { id: 2, name: "30 days", value: "30" },
      { id: 3, name: "60 days", value: "60" },
      { id: 4, name: "90 days", value: "90" },
      { id: 5, name: ">120 days", value: "gt120" },
    ],
    valueKey: "value",
    labelKey: "name",
    multiple: false,
  },
  start_date: { title: "Start Date", inputType: "date" },
  end_date: { title: "End Date", inputType: "date" },
  as_of_date: { title: "As of Date", inputType: "date" },
});

/** Map legacy or alternate API key names onto the canonical filter keys. */
const KEY_ALIASES = {
  lesser_name_id: "lessor",
  lessor_name_id: "lessor",
  asset_id: "asset",
  lessee_id: "lessee",
  owner_id: "owner",
};

const normalizeFilterKeyList = (keys) => {
  const seen = new Set();
  const out = [];
  for (const k of keys) {
    const t = String(k).trim();
    if (!t) continue;
    const canon = KEY_ALIASES[t] || t;
    if (!seen.has(canon)) {
      seen.add(canon);
      out.push(canon);
    }
  }
  return out;
};

/**
 * `query_json` from graph list: which filter fields this graph supports.
 * Accepts comma-separated string, JSON array string, plain array, object (keys = fields), or JSON object string.
 */
export const parseGraphFilterKeys = (queryJson) => {
  if (queryJson == null || queryJson === "") return [];

  if (typeof queryJson === "object" && !Array.isArray(queryJson)) {
    return normalizeFilterKeyList(Object.keys(queryJson));
  }

  if (Array.isArray(queryJson)) {
    return normalizeFilterKeyList(queryJson);
  }

  if (typeof queryJson === "string") {
    const trimmed = queryJson.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return parseGraphFilterKeys(JSON.parse(trimmed));
      } catch {
        /* fall through */
      }
    }
    return normalizeFilterKeyList(trimmed.split(",").map((s) => s.trim()));
  }

  return normalizeFilterKeyList(String(queryJson).split(","));
};

/** Preserve `query_json` key order (e.g. start_date before end_date). */
export const pickFilterMenuForKeys = (fullMenu, keys) => {
  if (!fullMenu || !keys?.length) return {};
  return keys.reduce((acc, key) => {
    if (fullMenu[key]) acc[key] = fullMenu[key];
    return acc;
  }, {});
};

export const cleanFilterQuery = (obj) =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(
      ([, v]) => v !== null && v !== undefined && v !== ""
    )
  );
